import type { ProductHistoryEntry } from "@shared/types";
import { Button } from "@shared-ui/button";
import { useInfiniteQuery } from "@tanstack/react-query";
import type { JSX } from "react";
import { uomLabel } from "@/features/catalog/lib/format";
import { getAdminProductHistory } from "../services/catalog-api.service";

/**
 * The Provenance tab: who changed this product, when, and what it was before.
 *
 * Everything the audit work wrote up to now has been invisible -- the rows
 * existed and nothing displayed them, which meant the one question the trail was
 * built to answer ("who changed this price") still required database access.
 *
 * Read-only by design. This tab reports history; it does not offer to revert,
 * because a revert is an ordinary edit that should itself be audited, and a
 * button that silently rewrote a product from a log entry would be the one
 * mutation with no operator intent behind it.
 */

/**
 * `catalog.product.updated` is the wire contract, not a label. Rendering the raw
 * string would put an implementation detail in front of an operator, and
 * translating it here keeps the persisted vocabulary free to stay terse.
 */
const ACTION_LABELS: Record<string, string> = {
	"catalog.product.created": "Created",
	"catalog.product.updated": "Edited",
	"catalog.product.deleted": "Deactivated",
	"catalog.product.image_uploaded": "Image uploaded",
	"catalog.variant.created": "Variant added",
	"catalog.variant.updated": "Variant edited",
	"catalog.variant.deleted": "Variant deleted",
	// Filed under the product by the manual price endpoint, so it lands here.
	"catalog.price_observation.recorded": "Price recorded",
};

/**
 * Field names come from the database column set, which an operator has never
 * seen. Anything unmapped falls back to the raw key rather than being hidden --
 * a change we cannot name is still a change they need to know about.
 */
const FIELD_LABELS: Record<string, string> = {
	name: "Name",
	description: "Description",
	sku: "SKU",
	brandSku: "Brand SKU",
	slug: "Slug",
	status: "Status",
	tier: "Tier",
	uom: "Unit",
	hsnCode: "HSN code",
	categoryId: "Category",
	brandId: "Brand",
	images: "Images",
	specifications: "Specifications",
	verification: "Verification",
	itemKind: "Item kind",
	trade: "Trade",
};

function actionLabel(action: string): string {
	return ACTION_LABELS[action] ?? action;
}

function fieldLabel(field: string): string {
	return FIELD_LABELS[field] ?? field;
}

/** Long values are truncated rather than allowed to blow up a row. */
const MAX_RENDERED = 160;

function truncate(text: string): string {
	return text.length > MAX_RENDERED
		? `${text.slice(0, MAX_RENDERED)}...`
		: text;
}

/**
 * Values in a diff are `unknown` -- they come from a JSONB column and can be a
 * string, a number, a boolean, null, or a whole object (`specifications`, and
 * `images` as an array).
 *
 * RENDER THE VALUE, NOT ITS SHAPE. The first version returned an array's LENGTH
 * and an object's KEYS, which meant the two edits an operator most needs to see
 * both rendered as no change at all: `{finish: "matte"} -> {finish: "glossy"}`
 * showed as `finish -> finish`, and swapping one image for another as
 * `1 item(s) -> 1 item(s)`. In an audit tab, displaying a real change as
 * unchanged is worse than displaying nothing.
 *
 * Empty is a word rather than blank space, because "" and "not set" look
 * identical in a table and mean different things in a history.
 */
function renderValue(field: string, value: unknown): string {
	if (value === null || value === undefined) return "not set";
	if (typeof value === "boolean") return value ? "yes" : "no";
	if (typeof value === "number") return String(value);
	if (typeof value === "string") {
		if (value.length === 0) return "empty";
		// The same value must not read as `sq ft` in the editor and `sq_ft` in the
		// history beside it.
		return field === "uom" ? uomLabel(value) : truncate(value);
	}
	if (Array.isArray(value)) {
		if (value.length === 0) return "none";
		return truncate(value.map((item) => renderValue(field, item)).join(", "));
	}
	if (typeof value === "object") {
		const entries = Object.entries(value as Record<string, unknown>);
		if (entries.length === 0) return "none";
		return truncate(
			entries.map(([key, v]) => `${key}: ${renderValue(key, v)}`).join(", "),
		);
	}
	return truncate(String(value));
}

/**
 * Absolute, not "3 hours ago".
 *
 * Relative time reads well and is useless in an audit: the question is always
 * "was this before or after the thing I am investigating", and that needs a
 * timestamp you can line up against something else.
 */
function renderWhen(iso: string): string {
	const at = new Date(iso);
	if (Number.isNaN(at.getTime())) return iso;
	return at.toLocaleString(undefined, {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

function HistoryRow({ entry }: { entry: ProductHistoryEntry }): JSX.Element {
	return (
		<li className="border-slate-200 border-b py-3 last:border-b-0">
			<div className="flex flex-wrap items-baseline justify-between gap-2">
				<span className="font-medium text-slate-900 text-sm">
					{actionLabel(entry.action)}
				</span>
				<span className="text-slate-500 text-xs">{renderWhen(entry.at)}</span>
			</div>

			<p className="mt-0.5 text-slate-600 text-xs">{entry.actor.label}</p>

			{entry.changes.length === 0 && entry.detail ? (
				/*
				 * Creates, deletes and uploads carry no diff, so without this they read
				 * as a bare action and a time -- "Variant deleted", with nothing saying
				 * which one. For a hard-deleted variant this SKU is the only handle
				 * anyone has left.
				 */
				<p className="mt-1 text-slate-700 text-xs">{entry.detail}</p>
			) : null}

			{entry.changes.length > 0 ? (
				<ul className="mt-2 space-y-1">
					{entry.changes.map((change) => (
						<li key={change.field} className="text-slate-700 text-xs">
							<span className="font-medium">{fieldLabel(change.field)}</span>
							{": "}
							<span className="text-slate-500 line-through">
								{renderValue(change.field, change.from)}
							</span>
							{" -> "}
							<span>{renderValue(change.field, change.to)}</span>
						</li>
					))}
				</ul>
			) : null}
		</li>
	);
}

export function ProductProvenance({
	productId,
}: {
	productId: string;
}): JSX.Element {
	const history = useInfiniteQuery({
		/*
		 * Namespaced to match what the editor invalidates.
		 *
		 * `product-editor.tsx` invalidates `["admin-catalog-product", productId]`
		 * after every save. The first version keyed this `["admin","catalog",
		 * "product",...]`, which shares no prefix with that -- so an operator saved
		 * a change and the history tab on the same page kept showing stale data for
		 * the app's 60s staleTime. An audit surface that lags the edit it is
		 * auditing is worse than no surface.
		 */
		queryKey: ["admin-catalog-product", productId, "history"],
		queryFn: ({ pageParam }) =>
			getAdminProductHistory(productId, { cursor: pageParam }),
		initialPageParam: undefined as string | undefined,
		getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
	});

	const entries = history.data?.pages.flatMap((page) => page.history) ?? [];

	if (history.isPending) {
		return <p className="text-slate-500 text-sm">Loading history...</p>;
	}

	if (history.isError && entries.length === 0) {
		return (
			<div className="space-y-2">
				<p className="text-slate-600 text-sm">
					The history could not be loaded. The product itself is unaffected --
					this tab only reads.
				</p>
				<Button variant="outline" size="sm" onClick={() => history.refetch()}>
					Try again
				</Button>
			</div>
		);
	}

	if (entries.length === 0) {
		/*
		 * Distinguishes "nothing has happened" from "we lost the record".
		 *
		 * A product created before this audit trail existed also shows nothing, and
		 * an operator reading a blank panel would reasonably conclude the feature is
		 * broken. Saying which it is costs one sentence.
		 */
		return (
			<p className="text-slate-500 text-sm">
				No recorded changes. Edits made before this product's history began
				tracking do not appear here.
			</p>
		);
	}

	return (
		<div>
			{history.isRefetchError ? (
				<div className="mb-3 flex items-center justify-between rounded-md bg-rose-50 px-3 py-2 text-rose-700 text-xs">
					<span>
						This history may be out of date — the last refresh failed.
					</span>
					<button
						type="button"
						onClick={() => history.refetch()}
						className="font-medium underline hover:text-rose-800"
					>
						Try again
					</button>
				</div>
			) : null}
			<ul className="divide-slate-100">
				{entries.map((entry) => (
					<HistoryRow key={entry.id} entry={entry} />
				))}
			</ul>
			{history.hasNextPage || history.isFetchNextPageError ? (
				<div className="mt-4 flex flex-col items-center gap-2 border-slate-200 border-t pt-3">
					{history.isFetchNextPageError ? (
						<>
							<p className="text-xs text-rose-600">
								Failed to load older entries.
							</p>
							<Button
								variant="outline"
								size="sm"
								disabled={history.isFetchingNextPage}
								onClick={() => history.fetchNextPage()}
							>
								{history.isFetchingNextPage ? "Retrying..." : "Try again"}
							</Button>
						</>
					) : (
						<Button
							variant="outline"
							size="sm"
							disabled={history.isFetchingNextPage}
							onClick={() => history.fetchNextPage()}
						>
							{history.isFetchingNextPage ? "Loading more..." : "Load more"}
						</Button>
					)}
				</div>
			) : null}
		</div>
	);
}
